'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import type { PopoverContentProps, PopoverProps } from '@radix-ui/react-popover'
import {
    TOOLTIP_ARROW_CLASS,
    TOOLTIP_CONTENT_CLASS,
    TOOLTIP_PORTAL_CONTAINER_CLASS,
} from '@trustoo/components/ui/tooltip/styles'
import { useTooltipPortalContainer } from '@trustoo/components/ui/tooltip/useTooltipPortalContainer'
import TopLayerPanel from '@trustoo/components/ui/TopLayerPanel'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

export type TooltipAsPopoverProps = {
    children: ReactNode
    childrenContent: ReactNode
    arrowClassName?: string
} & PopoverProps &
    PopoverContentProps &
    ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>

/**
 * Tooltip that opens on tap, for touch devices. The panel renders in the browser top layer next to
 * its trigger, so it is never clipped or covered by a modal; see `useTooltipPortalContainer`.
 */
export function TooltipAsPopover({
    children,
    childrenContent,
    open,
    defaultOpen,
    onOpenChange,
    arrowClassName,
    ...contentProps
}: TooltipAsPopoverProps) {
    const { isOpen, handleOpenChange, container, setContainer } = useTooltipPortalContainer({
        open,
        defaultOpen,
        onOpenChange,
    })

    return (
        <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
            {isOpen && (
                <TopLayerPanel ref={setContainer} className={TOOLTIP_PORTAL_CONTAINER_CLASS} />
            )}
            {container && (
                <PopoverPrimitive.Portal container={container}>
                    <PopoverPrimitive.Content
                        collisionPadding={16}
                        {...contentProps}
                        className={twMerge(
                            TOOLTIP_CONTENT_CLASS,
                            'max-w-[min(320px,var(--radix-popover-content-available-width))]',
                            contentProps.className,
                        )}>
                        {childrenContent}
                        <PopoverPrimitive.Arrow
                            className={twMerge(TOOLTIP_ARROW_CLASS, arrowClassName)}
                        />
                    </PopoverPrimitive.Content>
                </PopoverPrimitive.Portal>
            )}
        </PopoverPrimitive.Root>
    )
}
